import { useState, useEffect } from 'react';
import { socketService } from '../services/socket';
import { authFetch } from '../utils/authFetch';

interface Message {
  id: string;
  chatId: string;
  senderId?: string;
  content: string;
  messageType: 'TEXT' | 'IMAGE' | 'DOCUMENT' | 'AUDIO' | 'VIDEO';
  direction: 'INBOUND' | 'OUTBOUND';
  status: 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
  whatsappMessageId?: string;
  timestamp: string;
  createdAt: string;
}

export const useMessages = (chatId: string | null, userId?: string) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUserId, setTypingUserId] = useState<string | null>(null);

  const loadMessages = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // authFetch automatically prepends /api
      console.log('Loading messages for chat:', id, 'from: /chat/' + id + '/messages');
      
      const response = await authFetch(`/chat/${id}/messages`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Messages API response:', data);
        
        if (data.success && Array.isArray(data.data)) {
          if (data.data.length > 0) {
            setMessages(data.data);
            setError(null);
            
            // Mark messages as read when loading them
            markMessagesAsRead(id);
            return;
          } else {
            console.log('No messages in database, using mock data');
            throw new Error('No messages in database');
          }
        }
      }
      
      throw new Error('API not available');
    } catch (err) {
      console.log('Loading mock messages for chat:', id, 'Error:', err);
      if (err instanceof Error && err.message === 'Session expired') {
        return;
      }
      setError('Failed to load messages - ' + (err instanceof Error ? err.message : 'API connection failed'));
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async (id: string) => {
    if (!userId) return;
    
    try {
      // authFetch automatically prepends /api
      await authFetch(`/chat/${id}/read`, {
        method: 'POST',
        body: JSON.stringify({ userId }),
      });
    } catch (err) {
      console.error('Failed to mark messages as read:', err);
    }
  };

  const sendMessage = async (content: string, mediaUrl?: string, messageType?: string): Promise<void> => {
    if (!chatId) throw new Error('No chat selected');
    
    try {
      // authFetch automatically prepends /api
      const response = await authFetch(`/chat/${chatId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          content,
          messageType: messageType || 'TEXT',
          mediaUrl: mediaUrl || undefined
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMessages(prev => [...prev, data.data]);
          return;
        }
      }
      
      throw new Error('API send failed');
    } catch (err) {
      console.log('API send failed:', err);
      throw new Error('Failed to send message');
    }
  };

  // Socket.io real-time updates
  useEffect(() => {
    if (!chatId) return;

    // Connect to socket
    socketService.connect();

    // Listen for new messages in this chat
    const handleNewMessage = (data: any) => {
      console.log('Real-time message received:', data);
      if (data.chatId === chatId || data.message?.chatId === chatId) {
        setMessages(prev => {
          const messageToAdd = data.message || data;
          // Check if message already exists to avoid duplicates
          if (prev.find(m => m.id === messageToAdd.id)) {
            return prev;
          }
          return [...prev, messageToAdd];
        });
      }
    };

    // Listen for WhatsApp messages
    const handleWhatsAppMessage = (data: any) => {
      console.log('Real-time WhatsApp message:', data);
      if (data.chatId === chatId) {
        setMessages(prev => {
          if (prev.find(m => m.id === data.message.id)) {
            return prev;
          }
          return [...prev, data.message];
        });
        
        // Don't auto-mark incoming messages as read
        // They will be marked as read when user initially opens the chat via loadMessages
      }
    };

    // Listen for typing indicator
    const handleUserTyping = (data: { userId: string; chatId: string; isTyping: boolean }) => {
      if (data.chatId === chatId && data.userId !== userId) {
        setIsTyping(data.isTyping);
        setTypingUserId(data.isTyping ? data.userId : null);
        
        // Auto-clear typing indicator after 3 seconds
        if (data.isTyping) {
          setTimeout(() => {
            setIsTyping(false);
            setTypingUserId(null);
          }, 3000);
        }
      }
    };

    // Listen for read receipts
    const handleMessagesRead = (data: { chatId: string; userId: string; count: number }) => {
      if (data.chatId === chatId) {
        // Update message statuses to READ
        setMessages(prev => prev.map(msg => 
          msg.direction === 'OUTBOUND' && msg.status !== 'READ'
            ? { ...msg, status: 'READ' as const }
            : msg
        ));
      }
    };

    // Listen for WhatsApp message status updates (delivered, read, etc.)
    const handleMessageStatusUpdate = (data: { whatsappMessageId: string; status: string }) => {
      console.log('Message status update:', data);
      setMessages(prev => prev.map(msg => 
        msg.whatsappMessageId === data.whatsappMessageId
          ? { ...msg, status: data.status as any }
          : msg
      ));
    };

    socketService.on('new_message', handleNewMessage);
    socketService.on('new_whatsapp_message', handleWhatsAppMessage);
    socketService.on('user_typing', handleUserTyping);
    socketService.on('messages_read', handleMessagesRead);
    socketService.on('message_status_update', handleMessageStatusUpdate);

    // Join this chat room for targeted updates
    socketService.joinChat(chatId);

    return () => {
      socketService.off('new_message', handleNewMessage);
      socketService.off('new_whatsapp_message', handleWhatsAppMessage);
      socketService.off('user_typing', handleUserTyping);
      socketService.off('messages_read', handleMessagesRead);
      socketService.off('message_status_update', handleMessageStatusUpdate);
      socketService.leaveChat(chatId);
    };
  }, [chatId, userId]);

  useEffect(() => {
    if (chatId) {
      loadMessages(chatId);
    } else {
      setMessages([]);
    }
  }, [chatId]);

  return { 
    messages, 
    loading, 
    error, 
    sendMessage, 
    isTyping, 
    typingUserId,
    refetch: () => chatId && loadMessages(chatId) 
  };
};
